import React, { useMemo } from 'react';
import { ITableRowProps } from './types';
import { TABLE_COLUMN_KEYS } from './consts';
import { BODY_CELL_STYLES, WIDGET_MODE_BODY_CELL_STYLES } from '../../consts/styles';
import { calculateGrossAmount, formatDate, getNumOfOverdueDays } from '../../utils';
import { convertValueToUSD } from '../../../../../helpers/currency.helper';
import { DueDateNotice } from '../DueDateNotice';
import { useStore } from '../../../../../store';
import { ActionCell } from './ActionCell';
import { HUB_MODALS } from '../InvoiceHubModals';
import { INVOICE_STATUSES } from '../../consts/consts';
import { Avatar, AvatarSize } from '../../../../../DesignSystem/VendorProfile/Avatar';
import Heading from '../../../../../generic-components/Heading';
import { InvoicesStatusBadge } from '../InvoicesStatusBadge/InvoicesStatusBadge';

export const TableRow = ({ columnKeys, row, lastRow, isLastRow, isWidgetMode, onInvoiceRowClick }: ITableRowProps) => {
  const setSelectedInvoice = useStore((state) => state.setSelectedInvoice);
  const setActiveModal = useStore((state) => state.setActiveModal);
  const isOverDue = useMemo(
    () =>
      getNumOfOverdueDays(row.dueDate) &&
      ![INVOICE_STATUSES.PAID, INVOICE_STATUSES.SENT].includes(row.status) &&
      !row.paidTime,
    [row],
  );
  const renderInvoiceCellValue = () => (
    <>
      <div data-testid='invoiceNumber'>{`#${row.invoice}`}</div>
      <div>{formatDate(row.issueDate, true)}</div>
    </>
  );

  const renderDueDateCellValue = () => {
    if (!row?.status || !row?.dueDate) return null;
    return (
      <>
        <div>{`Due ${formatDate(row.dueDate, true)}`}</div>
        <DueDateNotice status={row.status} dueDate={row.dueDate} paidTime={row?.paidTime} />
      </>
    );
  };
  const renderAmountCellValue = () => {
    const grossAmount = calculateGrossAmount(row.amount, row?.discountType, row?.discount, row?.salesTax);
    return convertValueToUSD(grossAmount, 2);
  };

  const renderClientCell = () => (
    <div className='flex flex-row items-center gap-[6px]'>
      <Avatar name={row.client} id={row.clientId} size={AvatarSize.SMALL} />
      <Heading title={row.client} className=' text-bw08 font-metricR text-[14px]' />
    </div>
  );
  const renderCellValue = (columnKey: string) => {
    if (columnKey === TABLE_COLUMN_KEYS.INVOICE) return renderInvoiceCellValue();
    if (columnKey === TABLE_COLUMN_KEYS.CLIENT) return renderClientCell();
    if (columnKey === TABLE_COLUMN_KEYS.DUEDATE) return renderDueDateCellValue();
    if (columnKey === TABLE_COLUMN_KEYS.AMOUNT) return renderAmountCellValue();
    if (columnKey === TABLE_COLUMN_KEYS.STATUS) return <InvoicesStatusBadge status={row[columnKey]} />;
    if (columnKey === TABLE_COLUMN_KEYS.ACTION) return <ActionCell externalId={row.externalId} />;
    return null;
  };

  const renderBottomLeftRadius = (columnKey: string) =>
    (isLastRow && columnKey === TABLE_COLUMN_KEYS.INVOICE ? 'rounded-bl-lg' : '');
  const renderBottomRightRadius = (columnKey: string) =>
    (isLastRow && columnKey === TABLE_COLUMN_KEYS.ACTION ? 'rounded-br-lg' : '');
  const renderCell = (columnKey: string) => (
    <td id={`td_${columnKey}`} key={columnKey}>
      <div
        className={`${columnKey === TABLE_COLUMN_KEYS.AMOUNT && isOverDue ? 'text-red02' : ''} ${isWidgetMode ? WIDGET_MODE_BODY_CELL_STYLES[columnKey] : BODY_CELL_STYLES[columnKey]} ${!isWidgetMode ? renderBottomLeftRadius(columnKey) : ''} ${!isWidgetMode ? renderBottomRightRadius(columnKey) : ''}`}
      >
        {renderCellValue(columnKey)}
      </div>
    </td>
  );

  const onRowClick = () => {
    setSelectedInvoice(row.externalId);
    setActiveModal(HUB_MODALS.INVOICE_PAGE);
  };

  return (
    <tr
      ref={lastRow}
      className={`cursor-pointer relative hover:bg-bw02 bg-bw00 ${isWidgetMode ? 'w-full' : ''} ${!isLastRow && isWidgetMode ? 'border-bw03 border-b-[1px]' : ''}`}
      onClick={isWidgetMode ? () => onInvoiceRowClick && onInvoiceRowClick(row.externalId) : onRowClick}
    >
      {columnKeys.map((columnKey) => renderCell(columnKey))}
    </tr>
  );
};
